// src/pages/StampDutyCalc.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Container from "../components/Container.jsx";
import CalculatorModal from "../components/CalculatorModal.jsx";

/** Toggle whether to show the Q1/Q2/Q3 inputs on the page */
const SHOW_QUESTIONS = true; // set to false to hide inputs

export default function StampDutyCalc() {
  const calculator = "sdlt";

  const [open, setOpen] = useState(false);
  const [utms, setUtms] = useState({});
  const [price, setPrice] = useState("");         // Q1
  const [profile, setProfile] = useState(2);      // Q2: 1 FTB, 2 Mover, 3 Additional, 4 Commercial
  const [residency, setResidency] = useState(100);// Q3: 100 UK, 200 Overseas
  const [calcData, setCalcData] = useState(null); // { type:'completed', answers:{} }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const lastEvt = useRef(null);

  /* ---------- UTM helpers (same style as RetirementCalc) ---------- */
  function parseParams(str) {
    const out = {};
    const qs = new URLSearchParams(str || "");
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
      const v = qs.get(k);
      if (v) out[k] = v;
    }
    return out;
  }
  function readUtmsFromUrl() {
    const search = window.location.search || "";
    const hash = window.location.hash || "";
    const afterHash = hash.includes("?") ? hash.split("?")[1] : "";
    return { ...parseParams(search), ...parseParams(afterHash) };
  }
  function loadPersistedUtms() {
    try { return JSON.parse(sessionStorage.getItem("utm_params") || "{}"); } catch { return {}; }
  }
  function savePersistedUtms(obj) {
    try { sessionStorage.setItem("utm_params", JSON.stringify(obj)); } catch {}
  }
  useEffect(() => {
    const fromUrl = readUtmsFromUrl();
    const stored = loadPersistedUtms();
    const merged = { ...stored, ...fromUrl };
    setUtms(merged);
    savePersistedUtms(merged);
  }, []);

  /* ---------- Parse from URL (hash/query), auto-open like Retirement ---------- */
  function readParamsFromUrl() {
    const { hash, search } = window.location;
    const qs = hash.includes("?") ? hash.split("?")[1] : search.slice(1);
    return new URLSearchParams(qs);
  }

  function parseFromUrl() {
    const p = readParamsFromUrl();
    const answers = {
      Q1_price: Number(p.get("q1") || 0),
      Q2_profile: Number(p.get("q2") || 0),
      Q3_residency: Number(p.get("q3") || 0),
      base: Number(p.get("base") || 0),
      addl: Number(p.get("addl") || 0),
      nrs: Number(p.get("nrs") || 0),
      total: Number(p.get("total") || 0),
    };
    const any = Object.values(answers).some(v => Number(v) > 0);
    return any ? { type: "completed", answers } : null;
  }

  useEffect(() => {
    function handleHashChange() {
      const viaUrl = parseFromUrl();
      if (viaUrl) setCalcData(viaUrl);
      const p = readParamsFromUrl();
      if (p.get("og_open") === "1") setOpen(true);
    }
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    const viaUrl = parseFromUrl();
    const p = readParamsFromUrl();
    if (viaUrl) setCalcData(viaUrl);
    if (p.get("og_open") === "1") setTimeout(() => setOpen(true), 0);
  }, []);

  /* ---------- Calculator ---------- */
  const parsedPrice = Number(String(price).replace(/[, ]/g, "")) || 0;
  const result = useMemo(() => {
    try { return computeSDLT(parsedPrice, Number(profile), Number(residency)); }
    catch { return null; }
  }, [parsedPrice, profile, residency]);

  const nf = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(v || 0);

  function updateUrlForShare({ base, addl, nrs, total }) {
    try {
      const { origin, pathname, search, hash } = window.location;
      const [hashPath, hashQs] = (hash || "#").split("?");
      const params = new URLSearchParams(hashQs || search.slice(1));
      params.set("og_complete", "1");
      params.set("og_open", "1");
      params.set("calc", "sdlt");
      params.set("q1", String(parsedPrice));
      params.set("q2", String(profile));
      params.set("q3", String(residency));
      params.set("base", String(Math.round(base)));
      params.set("addl", String(Math.round(addl)));
      params.set("nrs", String(Math.round(nrs)));
      params.set("total", String(Math.round(total)));
      const newHash = `${hashPath}?${params.toString()}`;
      window.history.replaceState(null, "", `${origin}${pathname}${newHash}`);
    } catch {}
  }

  async function handleLearnMore() {
    if (!result) return;
    const answers = {
      Q1_price: parsedPrice,
      Q2_profile: Number(profile),
      Q3_residency: Number(residency),
      base: Math.round(result.base),
      addl: Math.round(result.surchargeAddl),
      nrs: Math.round(result.surchargeNRS),
      total: Math.round(result.total),
    };

    // 1) Update URL (no navigation)
    updateUrlForShare(answers);

    // 2) Set calc data and open the same CalculatorModal you already use
    setCalcData({ type: "completed", answers });
    setOpen(true);
  }

  return (
    <main className="py-10">
      <Container>
        <h1 className="text-3xl font-bold text-[#003366]">Stamp Duty Land Tax (SDLT)</h1>
        <p className="mt-2 text-slate-700">England & NI · 2025 rules.</p>

        {/* Inputs (optional) */}
        {SHOW_QUESTIONS && (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700">Property Price (£)</label>
              <input
                type="text"
                inputMode="decimal"
                className="mt-1 w-full rounded-lg ring-1 ring-slate-300 px-3 py-2"
                placeholder="e.g., 450000"
                value={price}
                onChange={e => setPrice(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Buyer Profile</label>
              <select
                className="mt-1 w-full rounded-lg ring-1 ring-slate-300 px-3 py-2 bg-white"
                value={profile}
                onChange={e => setProfile(Number(e.target.value))}
              >
                <option value={1}>First-time buyer</option>
                <option value={2}>Home mover (Selling previous home)</option>
                <option value={3}>Additional property</option>
                <option value={4}>Commercial (non-residential)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Residency</label>
              <select
                className="mt-1 w-full rounded-lg ring-1 ring-slate-300 px-3 py-2 bg-white"
                value={residency}
                onChange={e => setResidency(Number(e.target.value))}
              >
                <option value={100}>UK resident</option>
                <option value={200}>Overseas (non-resident)</option>
              </select>
            </div>
          </div>
        )}

        {/* Results card */}
        <div className="mt-6 rounded-2xl ring-1 ring-slate-200 bg-white p-6 shadow-sm">
          {result ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Row label="Base SDLT" value={nf(result.base)} />
                {result.surchargeAddl > 0 && <Row label="+ Additional property surcharge" value={nf(result.surchargeAddl)} />}
                {result.surchargeNRS > 0 && <Row label="+ Non-resident surcharge" value={nf(result.surchargeNRS)} />}
              </div>
              <div className="mt-4 border-t border-slate-200 pt-4">
                <div className="text-lg font-semibold text-[#003366]">
                  Total SDLT payable: {nf(result.total)}
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={handleLearnMore}
                  disabled={busy || !parsedPrice}
                  className="px-6 py-3 rounded-xl text-white disabled:opacity-50"
                  style={{ background: "#003366" }}
                >
                  {busy ? "Preparing…" : "Learn more"}
                </button>
                {err && <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
              </div>
            </>
          ) : (
            <div className="text-slate-600">Enter inputs to see the result.</div>
          )}
        </div>

        {/* Same modal component you use for Retirement */}
        <CalculatorModal
          open={open}
          onClose={() => setOpen(false)}
          calculator={calculator}
          outgrowUrl=""   // not used here, but prop exists
          calcData={calcData}
          utms={utms}
        />
      </Container>
    </main>
  );
}

/* ---------- small row ---------- */
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-slate-600">{label}</div>
      <div className="font-medium text-slate-900">{value}</div>
    </div>
  );
}

/* ---------- SDLT engine (England & NI, 2025) ---------- */
function computeSDLT(P, Q2, Q3) {
  const RES_STD = [
    [0, 125000, 0.00],
    [125000, 250000, 0.02],
    [250000, 925000, 0.05],
    [925000, 1500000, 0.10],
    [1500000, Infinity, 0.12],
  ];
  const FTB = [
    [0, 300000, 0.00],
    [300000, 500000, 0.05],
  ];
  const COM = [
    [0, 150000, 0.00],
    [150000, 250000, 0.02],
    [250000, Infinity, 0.05],
  ];
  const addl = 0.05; // additional property surcharge
  const nrs  = 0.02; // non-resident surcharge

  const useFTB = (Q2 === 1) && P <= 500000;
  const residential = Q2 !== 4;

  const base = Q2 === 4 ? tierTax(P, COM)
                        : useFTB ? tierTax(P, FTB) : tierTax(P, RES_STD);

  const resPortion = residential
    ? (useFTB ? tierPortion(P, FTB) : tierPortion(P, RES_STD))
    : 0;

  const surchargeAddl = (Q2 === 3) ? resPortion * addl : 0;
  const surchargeNRS  = (residential && Q3 === 200) ? resPortion * nrs : 0;

  const total = base + surchargeAddl + surchargeNRS;
  return { base, surchargeAddl, surchargeNRS, total };
}
function tierTax(P, tiers) {
  let tax = 0;
  for (const [a, b, r] of tiers) tax += Math.max(0, Math.min(P, b) - a) * r;
  return tax;
}
function tierPortion(P, tiers) {
  let portion = 0;
  for (const [a, b] of tiers) portion += Math.max(0, Math.min(P, b) - a);
  return portion;
}
